import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../utils/cookieUtils";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../data/enums/TokenEnums";
import { OBJECTIVE_TYPE } from "../_config/quarterlyOkrConfig";
import { QOKR_URLS } from "./quarterlyOkrUrls";

// ─────────────────────────────────────────────────────────────────────────────
// Quarterly OKR — the coach's REST data layer (§9, §12).
//
// Vanilla zustand + the hand-rolled selector hook, per CLAUDE.md. Server data
// is cached by `quarterCompanyId` (`reviewByQc` etc.) so switching between
// clients is instant; `{ force }` bypasses. One `pending[key]` map instead of a
// flag per action, so a mark PUT on one row never disables another.
//
// Reads reject and the screen decides what to show; writes reject too, so the
// caller can branch on the error `code` (`readError`) — a 409
// `unmarked_objectives` and a 400 `invalid_meeting` need different answers.
// ─────────────────────────────────────────────────────────────────────────────

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

// Copied from `AnnualOkrStore._resolveAuthHeader`, as CLAUDE.md asks: access
// token first, else the admin-impersonating-a-coach pair.
const resolveAuthHeader = () => {
  const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
  const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
  const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
  if (accessToken) return `Bearer ${accessToken}`;
  if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
  return null;
};

const request = (method, url, body) => {
  const authHeader = resolveAuthHeader();
  const config = { headers: authHeader ? { Authorization: authHeader } : {} };
  const call =
    method === "get" || method === "delete"
      ? nodeInstance[method](url, config)
      : nodeInstance[method](url, body ?? {}, config);
  // The QOKR routes answer with the payload itself (`{ clients }`), not the
  // `{ message, data }` wrapper most coach routes use — read either.
  return call.then(({ data: payload } = {}) =>
    payload && typeof payload === "object" && "data" in payload
      ? payload.data
      : (payload ?? null),
  );
};

// ─── Review helpers ─────────────────────────────────────────────────────────
const listFor = (type) =>
  type === OBJECTIVE_TYPE.COMPANY
    ? "company_objectives"
    : "individual_objectives";

const mapObjective = (review, type, objectiveId, update) => ({
  ...review,
  [listFor(type)]: (review[listFor(type)] || []).map((objective) =>
    String(objective.objective_id) === String(objectiveId)
      ? update(objective)
      : objective,
  ),
});

// Mark PUTs return only the row, so `counts` (the header and the submit gate)
// is recomputed here with the server's own rule: an objective is marked once
// it has a review status.
export const recomputeCounts = (review) => {
  const all = [
    ...(review.company_objectives || []),
    ...(review.individual_objectives || []),
  ];
  const marked = all.filter((objective) => objective.review?.status);
  return {
    ...review,
    counts: {
      total: all.length,
      marked: marked.length,
      to_mark: all.length - marked.length,
      to_discuss: marked.filter((o) => o.review.status === "to_discuss").length,
    },
  };
};

const INITIAL_STATE = {
  clients: null, // §9.1 — null until the first fetch, then an array
  reviewByQc: {},
  summaryByQc: {},
  briefByQc: {}, // handoff 34 — the coach brief response, polled
  trackingByQc: {}, // the scorecard (see `QOKR_URLS.TRACKING`)
  pending: {},
};

const quarterlyOkrStore = createStore((set, get) => {
  const track = (key, promise) => {
    set((s) => ({ pending: { ...s.pending, [key]: true } }));
    return promise.finally(() =>
      set((s) => {
        const pending = { ...s.pending };
        delete pending[key];
        return { pending };
      }),
    );
  };

  const putIn = (field, qcId, value) =>
    set((s) => ({ [field]: { ...s[field], [qcId]: value } }));

  const patchReview = (qcId, update) =>
    set((s) => {
      const current = s.reviewByQc[qcId];
      if (!current) return {};
      return {
        reviewByQc: {
          ...s.reviewByQc,
          [qcId]: recomputeCounts(update(current)),
        },
      };
    });

  // Cached read: the stored copy unless `force`.
  const read = (field, url, qcId, { force = false } = {}) => {
    if (!force && get()[field][qcId])
      return Promise.resolve(get()[field][qcId]);
    return request("get", url).then((data) => {
      putIn(field, qcId, data);
      return data;
    });
  };

  return {
    ...INITIAL_STATE,

    // ── §9.1 ─────────────────────────────────────────────────────────────────
    fetchDashboard: () =>
      track(
        "dashboard",
        request("get", QOKR_URLS.DASHBOARD).then((data) => {
          const clients = Array.isArray(data)
            ? data
            : Array.isArray(data?.clients)
              ? data.clients
              : [];
          set({ clients });
          return clients;
        }),
      ),

    // ── §9.3 ─────────────────────────────────────────────────────────────────
    // A 409 here means the run moved past review; the GET after it says so
    // more usefully than a toast before the screen has painted.
    enterReview: (qcId) =>
      request("post", QOKR_URLS.REVIEW_ENTER(qcId)).catch(() => null),

    fetchReview: (qcId, options) =>
      read("reviewByQc", QOKR_URLS.REVIEW(qcId), qcId, options),

    // Optimistic, and rolls back to the PREVIOUS mark rather than clearing it
    // (CLAUDE.md): clearing would silently move the submit gate.
    setMark: (qcId, type, objectiveId, status) => {
      const review = get().reviewByQc[qcId];
      const previous =
        (review?.[listFor(type)] || []).find(
          (o) => String(o.objective_id) === String(objectiveId),
        )?.review || null;
      const apply = (mark) =>
        patchReview(qcId, (current) =>
          mapObjective(current, type, objectiveId, (objective) => ({
            ...objective,
            review: mark,
          })),
        );

      apply({ ...previous, status });
      return track(
        `mark:${type}:${objectiveId}`,
        request("put", QOKR_URLS.REVIEW_STATUS(qcId, type, objectiveId), {
          status,
        }),
      ).then(
        (saved) => {
          if (saved?.status && saved.status !== status) {
            apply({ ...previous, status: saved.status });
          }
          return saved;
        },
        (error) => {
          apply(previous);
          throw error;
        },
      );
    },

    // Not optimistic: the server mints the id, and a comment without one
    // can't be deleted.
    addComment: (qcId, body, pendingKey) =>
      track(
        pendingKey,
        request("post", QOKR_URLS.REVIEW_COMMENTS(qcId), body).then((saved) => {
          patchReview(qcId, (current) => ({
            ...current,
            comments: [...(current.comments || []), { ...body, ...saved }],
          }));
          return saved;
        }),
      ),

    deleteComment: (qcId, commentId) =>
      track(
        `deleteComment:${commentId}`,
        request("delete", QOKR_URLS.REVIEW_COMMENT(qcId, commentId)).then(() =>
          patchReview(qcId, (current) => ({
            ...current,
            comments: (current.comments || []).filter(
              (c) => String(c.coach_comment_id) !== String(commentId),
            ),
          })),
        ),
      ),

    // The run's state moves, and it gates the whole screen — refetch rather
    // than patch.
    submitReview: (qcId, body) =>
      track(
        "submit",
        request("post", QOKR_URLS.REVIEW_SUBMIT(qcId), body).then(
          async (data) => {
            await get().fetchReview(qcId, { force: true });
            return data;
          },
        ),
      ),

    // ── §9.5 ─────────────────────────────────────────────────────────────────
    startLiveReview: (qcId) =>
      track("startLive", request("post", QOKR_URLS.LIVE_REVIEW_START(qcId))),

    // ── Coach brief (handoff 34) ─────────────────────────────────────────────
    // Always fresh: the screen polls this while the brief is being written.
    fetchBrief: (qcId) =>
      read("briefByQc", QOKR_URLS.BRIEF(qcId), qcId, { force: true }),

    // Puts the cached copy into IN_PROGRESS straight away so the screen
    // swaps to the "being prepared" state and starts polling.
    regenerateBrief: (qcId) =>
      track(
        `regenerate:${qcId}`,
        request("post", QOKR_URLS.BRIEF_REGENERATE(qcId)),
      ).then((data) => {
        const current = get().briefByQc[qcId] || {};
        putIn("briefByQc", qcId, {
          ...current,
          status: data?.status || "IN_PROGRESS",
          brief: null,
        });
        return data;
      }),

    // ── §12 ──────────────────────────────────────────────────────────────────
    fetchSummary: (qcId) =>
      read("summaryByQc", QOKR_URLS.SUMMARY(qcId), qcId, { force: true }),

    // ── Scorecard (locked quarter) ───────────────────────────────────────────
    // Cached per quarter for instant client switches; `force` on every open
    // so the progress the team logged since is shown.
    fetchTracking: (qcId) =>
      read("trackingByQc", QOKR_URLS.TRACKING(qcId), qcId, { force: true }),
  };
});

// Same hand-rolled selector hook as the other coach stores. Selectors must be
// defined at module scope.
export const useQuarterlyOkrStore = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(quarterlyOkrStore.getState()),
  );
  useEffect(
    () =>
      quarterlyOkrStore.subscribe((state) => {
        const next = selector(state);
        setSelected((prev) => (prev === next ? prev : next));
      }),
    [selector],
  );
  return selected;
};

export default quarterlyOkrStore;
